/**
 * Heading — titre choisi par son rôle, pas par sa taille (SAAS-1182).
 *
 * @remarks
 * Trois rôles, branchés sur les tokens `--text-*-title` (`tokens.css`) qui
 * suivent le réglage Font Size :
 *
 * | `level`   | balise par défaut | token                 | à 15px | usage                        |
 * | --------- | ----------------- | --------------------- | ------ | ---------------------------- |
 * | `page`    | `h1`              | `--text-page-title`   | ~21px  | titre de la vue              |
 * | `section` | `h2`              | `--text-section-title`| ~17px  | bloc d'une page              |
 * | `card`    | `h3`              | `--text-card-title`   | ~15px  | carte, panneau, élément      |
 *
 * Avant, 31 `<h1>` s'écrivaient en 10 tailles (souvent plus petites que le
 * corps de texte) et 35 `<h2>` en 6. Graisse plafonnée à `semibold` : la
 * police des titres (Schibsted) n'existe pas en 700, un `font-bold` y était
 * synthétisé.
 *
 * `as` change la balise sans changer le rôle visuel, pour respecter la
 * hiérarchie du document (une carte dans une section peut être un `h4`).
 */

import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

export type HeadingLevel = 'page' | 'section' | 'card';
type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level: HeadingLevel;
  /** Balise HTML, si elle diffère de celle du rôle (hiérarchie du document). */
  as?: HeadingTag;
}

const TAG: Record<HeadingLevel, HeadingTag> = { page: 'h1', section: 'h2', card: 'h3' };

const SIZE: Record<HeadingLevel, string> = {
  page: 'text-page-title',
  section: 'text-section-title',
  card: 'text-card-title',
};

export function Heading({ level, as, className, ...rest }: HeadingProps) {
  const Tag = as ?? TAG[level];
  return <Tag className={cn('text-fg m-0 font-semibold', SIZE[level], className)} {...rest} />;
}
